import type { ComponentChildren } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';

import { gridArrow, gridClamp, gridLift, gridPlace, gridResize } from '@amumin/domain/grid';
import {
  HOME_CARD_SIZES,
  HOME_GRID,
  HOME_GRID_GAP_PX,
  HOME_GRID_ROW_PX,
  HOME_GRID_TABLET,
  homeCardPreset,
  homeCardSizeForPx,
  homeCardSizeOf,
  moveWidget,
  placeWidget,
  projectHomeGrid,
  unprojectHomeGrid,
  type HomeCard,
  type HomeCardSize,
  type WidgetId,
} from '@amumin/domain/widgets';
import { t } from '@amumin/i18n';
import {
  gridDragStart,
  gridDragStill,
  Icon,
  startGridDrag,
  type GridDragVia,
} from '@amumin/ui-core';

import { appText } from '../state/session.js';
import { fitCards } from './cardFit.js';

// Vandaag als raster (feat/vandaag-raster; oprichter 5 okt 2026: "net zoals het bord aanpasbaar met
// een raster"). Drie standen, gekozen op de breedte van de pagina (niet van het venster: de zijbalk
// telt mee):
//   - computer (>= 900 px): 12 kolommen, rijen van 56 px; elke kaart op zijn eigen plek en maat;
//   - tablet (560 tot 900 px): 6 kolommen, omgerekend uit de 12 (domain projectHomeGrid);
//   - telefoon: één kolom in de volgorde van de lijst, de kaarten zo hoog als hun inhoud.
// In Aanpassen ligt over elke kaart een kader: slepen aan het kader verplaatst, de grepen aan de
// eindrand, de onderrand en de hoek maken smaller, breder, lager of hoger, en S, M en L zijn snelle
// maten. Toetsenbord: pijltjes verplaatsen, Shift + pijltjes maken kleiner of groter. Botsen,
// duwen en ruilen doet dezelfde motor als op het bord (@amumin/domain/grid); het slepen zelf de
// gedeelde bediening uit ui-core (startGridDrag). Op de telefoon alleen de volgorde: omhoog,
// omlaag of slepen aan het greepje.
//
// Een kaart zonder inhoud (geen open ritten, niemand spaart) laat buiten Aanpassen geen gat achter:
// wat eronder stond, schuift omhoog (gridLift). De maat van de inhoud (S/M/L, data-size) volgt uit
// de echte grootte van de kaart in px: klein is compact, groot toont meer.

export type TodayGridMode = 'list' | 'tablet' | 'desk';
type Mode = TodayGridMode;
type Handle = 'move' | 'e' | 's' | 'se';

interface Drag {
  id: WidgetId;
  target: HomeCard;
  result: HomeCard[] | null;
}

interface Props {
  /** De kaarten in de volgorde van de telefoon. */
  order: WidgetId[];
  /** Dezelfde kaarten in het raster van 12 kolommen. */
  cards: HomeCard[];
  editing: boolean;
  name: (id: WidgetId) => string;
  render: (id: WidgetId, size: HomeCardSize) => ComponentChildren;
  /** Nieuw raster (12 kolommen) na verplaatsen of een andere maat. */
  onCards: (next: HomeCard[], id: WidgetId) => void;
  /** Nieuwe volgorde op de telefoon. */
  onOrder: (next: WidgetId[], id: WidgetId) => void;
  onRemove: (id: WidgetId) => void;
  /** Voor schermlezers (aria-live in Today). */
  say: (text: string) => void;
  /** De stand die nu geldt (voor de uitleg boven Aanpassen). */
  onMode?: ((mode: TodayGridMode) => void) | undefined;
}

/**
 * Waar "+n meer" heen gaat als een kaart niet alles kan laten zien (geen scroll in kaarten, 6 okt).
 * Zonder adres opent de regel wat de kaart zelf opent ([data-fit-open], bv. het weerdetail) of de
 * link in de kop.
 */
const MORE_HREF: Partial<Record<WidgetId, string>> = {
  now: '/agenda',
  agenda: '/agenda',
  tasks: '/taken',
  tomorrow: '/agenda',
  rides: '/ritten',
  prayer: '/instellingen/gezin/extra',
  goals: '/beloningen',
  shopping: '/eten/boodschappen',
  messages: '/berichten',
  wishes: '/wensen',
  rotation: '/rooster',
  dinner: '/wie-eet-mee',
  bookings: '/reserveren',
  balance: '/kosten',
  money: '/geldzaken',
  todos: '/to-do',
  house_round: '/eten/huisronde',
};

const modeFor = (w: number): Mode => (w >= 900 ? 'desk' : w >= 560 ? 'tablet' : 'list');

/** Breedte van de pagina-inhoud, gemeten (en opnieuw bij elke andere maat). */
function useWidth(): [(el: HTMLDivElement | null) => void, number, HTMLDivElement | null] {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!node) return undefined;
    const measure = () => setWidth(node.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [node]);
  return [setNode, width, node];
}

export function TodayGrid(props: Props) {
  const { order, cards, editing, name, render } = props;
  const h = appText.value.home;
  const [ref, width, node] = useWidth();
  const root = useRef<HTMLDivElement | null>(null);
  const grid = useRef<HTMLDivElement>(null);
  const [empty, setEmpty] = useState('');
  const [draft, setDraft] = useState<HomeCard[] | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const cancel = useRef<(() => void) | null>(null);
  const pendingFocus = useRef<{ id: WidgetId; action: string } | null>(null);
  const latest = useRef(props);
  latest.current = props;
  useEffect(() => () => cancel.current?.(), []);
  useEffect(() => {
    if (!editing) cancel.current?.();
  }, [editing]);

  // Welke kaarten nu niets laten zien: na elke render en bij elke wijziging in de inhoud (een
  // widget laadt zijn eigen gegevens) opnieuw bekeken. Alleen een andere uitkomst tekent opnieuw.
  const checkEmpty = () => {
    const el = root.current;
    if (!el) return;
    const ids: string[] = [];
    el.querySelectorAll<HTMLElement>('[data-card-body]').forEach((body) => {
      if (body.childElementCount === 0 && !(body.textContent ?? '').trim()) {
        const id = body.getAttribute('data-card-body');
        if (id) ids.push(id);
      }
    });
    const key = ids.sort().join(',');
    setEmpty((cur) => (cur === key ? cur : key));
  };
  // Geen scroll in kaarten (6 okt 2026): in het raster laat elke kaart zien wat past, de rest staat
  // achter één regel "+n meer" (home/cardFit.ts). Opnieuw na elke render, als een widget zijn
  // gegevens heeft geladen en als de maat verandert. De eigen wijzigingen (de tekst van de regel)
  // tellen niet als nieuwe inhoud (takeRecords).
  const fit = () => {
    const el = grid.current;
    if (!el) return;
    const text = appText.value.home;
    fitCards(el, { more: (count) => t(text.fitMore, { count }), all: text.fitMoreAll });
  };
  useLayoutEffect(() => {
    checkEmpty();
    fit();
  });
  useEffect(() => {
    if (!node) return undefined;
    let raf = 0;
    const later = () => {
      if (!raf)
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          checkEmpty();
          fit();
          mo.takeRecords();
        });
    };
    const mo = new MutationObserver(later);
    mo.observe(node, { childList: true, subtree: true, characterData: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(later);
    ro?.observe(node);
    void document.fonts?.ready.then(later);
    return () => {
      mo.disconnect();
      ro?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [node]);
  const emptySet = empty ? empty.split(',') : [];
  const isEmpty = (id: WidgetId) => emptySet.indexOf(id) >= 0;

  // Na een wijziging met het toetsenbord of een knop: de focus terug op dezelfde kaart.
  useEffect(() => {
    const f = pendingFocus.current;
    if (!f || !root.current) return;
    const el =
      root.current.querySelector<HTMLElement>(
        `[data-widget="${f.id}"] [data-action="${f.action}"]:not([disabled])`,
      ) ??
      root.current.querySelector<HTMLElement>(
        `[data-widget="${f.id}"] [data-action]:not([disabled])`,
      );
    if (el && document.activeElement !== el) el.focus();
    pendingFocus.current = null;
  });

  const mode: Mode = width > 0 ? modeFor(width) : 'list';
  useEffect(() => {
    if (width > 0) latest.current.onMode?.(mode);
  }, [mode, width > 0]);
  const setRoot = (el: HTMLDivElement | null) => {
    root.current = el;
    ref(el);
  };

  if (mode === 'list') {
    return (
      <div
        ref={setRoot}
        class={editing ? 'home-list is-editing' : 'home-list'}
        data-name="home-grid"
        data-mode="list"
        data-editing={editing ? 'true' : undefined}
      >
        <TodayList
          {...props}
          isEmpty={isEmpty}
          onFocus={(id, action) => (pendingFocus.current = { id, action })}
        />
      </div>
    );
  }

  const spec = mode === 'desk' ? HOME_GRID : HOME_GRID_TABLET;
  const shown = mode === 'desk' ? cards : projectHomeGrid(cards);
  const live = editing ? (draft ?? shown) : gridLift(shown, isEmpty);
  const visible = live.filter((c) => editing || !isEmpty(c.kind));
  const bottom = visible.reduce((m, c) => Math.max(m, c.y + c.h), 0);
  const rows = Math.max(1, editing ? bottom + 3 : bottom);
  const cols = spec.cols;
  const cellW = (width - HOME_GRID_GAP_PX * (cols - 1)) / cols;
  const px = (n: number, cell: number) => n * cell + (n - 1) * HOME_GRID_GAP_PX;
  const rtl = () => !!grid.current && getComputedStyle(grid.current).direction === 'rtl';
  const where = (c: HomeCard) =>
    t(h.cardAt, { name: name(c.kind), w: c.w, h: c.h, x: c.x + 1, y: c.y + 1 });

  /** Een nieuw raster in de kolommen van nu: terug naar 12 kolommen en opslaan. */
  const commit = (next: HomeCard[] | null, id: WidgetId, action: string) => {
    if (!next) {
      props.say(h.noRoom);
      return;
    }
    const now = mode === 'desk' ? cards : projectHomeGrid(cards);
    const full = mode === 'desk' ? next : unprojectHomeGrid(cards, now, next);
    pendingFocus.current = { id, action };
    latest.current.onCards(full, id);
    const c = next.find((it) => it.kind === id);
    if (c) props.say(where(c));
  };

  const begin = (card: HomeCard, handle: Handle, x: number, y: number, via: GridDragVia) => {
    cancel.current?.();
    const el = grid.current;
    if (!el) return;
    const start = shown.slice();
    let last: Drag | null = null;
    cancel.current = startGridDrag({
      el,
      cols,
      rows,
      gap: HOME_GRID_GAP_PX,
      x,
      y,
      via,
      rtl: rtl(),
      autoScroll: true,
      onStep: (dc, dr) => {
        let target: HomeCard;
        let result: HomeCard[] | null;
        if (handle === 'move') {
          target = gridClamp(spec, { ...card, x: card.x + dc, y: card.y + dr });
          result = gridPlace(spec, start, target);
        } else {
          const w = handle === 's' ? card.w : card.w + dc;
          const hh = handle === 'e' ? card.h : card.h + dr;
          result = gridResize(spec, start, card.kind, w, hh);
          target =
            (result && result.find((c) => c.kind === card.kind)) ||
            gridClamp(spec, {
              ...card,
              w: Math.min(w, cols - card.x),
              h: Math.max(1, hh),
            });
        }
        last = { id: card.kind, target, result };
        setDrag(last);
        setDraft(result ?? start);
      },
      onEnd: (save, moved) => {
        cancel.current = null;
        setDrag(null);
        setDraft(null);
        if (save && moved && last) commit(last.result, card.kind, 'move');
      },
    });
  };

  const key = (card: HomeCard, e: KeyboardEvent) => {
    const step = gridArrow(e.key, rtl());
    if (!step) return;
    e.preventDefault();
    const next = e.shiftKey
      ? gridResize(spec, shown, card.kind, card.w + step[0], card.h + step[1])
      : gridPlace(spec, shown, { ...card, x: card.x + step[0], y: card.y + step[1] });
    const moved = next && next.find((c) => c.kind === card.kind);
    if (
      moved &&
      moved.x === card.x &&
      moved.y === card.y &&
      moved.w === card.w &&
      moved.h === card.h
    ) {
      props.say(where(card));
      return;
    }
    commit(next, card.kind, 'move');
  };

  /** S, M of L: de vaste maat (in de kolommen van nu), zo nodig wat naar de beginkant geschoven. */
  const preset = (card: HomeCard, size: HomeCardSize) => {
    const p = homeCardPreset(card.kind, size);
    const w = mode === 'desk' ? p.w : Math.ceil(p.w / 2);
    const next = gridPlace(spec, shown, {
      ...card,
      x: Math.min(card.x, cols - w),
      w,
      h: p.h,
    });
    commit(next, card.kind, `size-${size}`);
  };

  const cells = [];
  if (editing) {
    for (let r = 0; r < rows; r += 1)
      for (let c = 0; c < cols; c += 1)
        cells.push(
          <span
            key={`${r}-${c}`}
            class="home-raster-cell"
            aria-hidden="true"
            style={{ gridColumn: `${c + 1} / span 1`, gridRow: `${r + 1} / span 1` }}
          />,
        );
  }

  return (
    <div
      ref={setRoot}
      class="home-raster-wrap"
      data-name="home-grid"
      data-mode={mode}
      data-editing={editing ? 'true' : undefined}
    >
      <div
        ref={grid}
        class={editing ? 'home-raster is-editing' : 'home-raster'}
        role={editing ? 'group' : undefined}
        aria-label={editing ? h.gridHint : undefined}
        data-cols={cols}
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, ${HOME_GRID_ROW_PX}px)`,
        }}
      >
        {cells}
        {order.map((id) => {
          // Een lege kaart blijft (verborgen) in de pagina: zo zien we het als hij inhoud krijgt.
          const base = live.find((c) => c.kind === id) ?? shown.find((c) => c.kind === id);
          if (!base) return null;
          const dragging = drag?.id === id;
          const card = dragging ? drag.target : base;
          const blocked = dragging && !drag.result;
          const hidden = !editing && isEmpty(id);
          const size = homeCardSizeForPx(px(card.w, cellW), px(card.h, HOME_GRID_ROW_PX));
          const full = cards.find((c) => c.kind === id);
          const asked = full ? homeCardSizeOf(full.w, full.h) : size;
          const label = name(id);
          return (
            <section
              key={id}
              class={`home-card${dragging ? ' is-active' : ''}${blocked ? ' is-blocked' : ''}`}
              data-widget={id}
              data-size={size}
              data-x={card.x}
              data-y={card.y}
              data-w={card.w}
              data-h={card.h}
              hidden={hidden}
              aria-label={editing ? label : undefined}
              style={{
                gridColumn: `${card.x + 1} / span ${card.w}`,
                gridRow: `${card.y + 1} / span ${card.h}`,
              }}
            >
              <div
                class="home-card-body"
                data-card-body={id}
                data-quiet={h.quiet}
                inert={editing}
                aria-hidden={editing ? 'true' : undefined}
              >
                {render(id, size)}
              </div>
              {/* Wat niet past: één regel naar de pagina erachter (tekst en plek zet cardFit). */}
              {MORE_HREF[id] ? (
                <a
                  class="home-fit-more"
                  href={MORE_HREF[id]}
                  data-name={`home-more-${id}`}
                  tabIndex={editing ? -1 : undefined}
                  aria-hidden={editing ? 'true' : undefined}
                />
              ) : (
                <button
                  type="button"
                  class="home-fit-more"
                  data-name={`home-more-${id}`}
                  tabIndex={editing ? -1 : undefined}
                  aria-hidden={editing ? 'true' : undefined}
                  onClick={(e) => {
                    const card = (e.currentTarget as HTMLElement).closest('.home-card');
                    card
                      ?.querySelector<HTMLElement>('[data-fit-open], .section-head a[href]')
                      ?.click();
                  }}
                />
              )}
              {editing ? (
                <div class="home-frame">
                  <button
                    type="button"
                    class="home-frame-move"
                    aria-label={`${where(base)}. ${h.keysHint}`}
                    data-action="move"
                    data-name={`home-move-${id}`}
                    onKeyDown={(e) => key(base, e)}
                    {...gridDragStart((x, y, via) => begin(base, 'move', x, y, via))}
                  />
                  <div class="home-frame-chip" {...gridDragStill}>
                    <span class="home-frame-name">{label}</span>
                    <span class="home-frame-dims" dir="ltr" aria-hidden="true">
                      {card.w}×{card.h}
                    </span>
                    <button
                      type="button"
                      class="home-frame-btn"
                      aria-label={t(h.remove, { name: label })}
                      title={t(h.remove, { name: label })}
                      data-action="remove"
                      data-name={`home-remove-${id}`}
                      onClick={() => props.onRemove(id)}
                    >
                      <Icon id="x" size={18} />
                    </button>
                  </div>
                  <div
                    class="home-frame-sizes"
                    role="group"
                    aria-label={`${h.size}: ${label}`}
                    {...gridDragStill}
                  >
                    {HOME_CARD_SIZES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        class="home-frame-size"
                        aria-pressed={asked === s}
                        aria-label={`${label}: ${h.sizes[s]}`}
                        title={h.sizes[s]}
                        data-action={`size-${s}`}
                        data-name={`home-size-${id}-${s}`}
                        onClick={() => preset(base, s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {blocked ? <span class="home-frame-noroom">{h.noRoom}</span> : null}
                  <span
                    class="home-frame-e"
                    aria-hidden="true"
                    data-name={`home-resize-e-${id}`}
                    {...gridDragStart((x, y, via) => begin(base, 'e', x, y, via))}
                  />
                  <span
                    class="home-frame-s"
                    aria-hidden="true"
                    data-name={`home-resize-s-${id}`}
                    {...gridDragStart((x, y, via) => begin(base, 's', x, y, via))}
                  />
                  <span
                    class="home-frame-se"
                    aria-hidden="true"
                    data-name={`home-resize-se-${id}`}
                    {...gridDragStart((x, y, via) => begin(base, 'se', x, y, via))}
                  />
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ---- Telefoon: één kolom, alleen de volgorde -------------------------------------------------

interface ListProps extends Props {
  isEmpty: (id: WidgetId) => boolean;
  onFocus: (id: WidgetId, action: string) => void;
}

interface ListDrag {
  id: WidgetId;
  pointerId: number;
  start: WidgetId[];
}

/**
 * De telefoon: de kaarten onder elkaar in hun volgorde, zo hoog als hun inhoud. In Aanpassen per
 * kaart een greepje om te slepen (muis, pen en vinger; touch-action: none op het greepje) en knoppen
 * voor wie niet sleept: omhoog, omlaag, weghalen. Tijdens het slepen schuift de lijst live mee; pas
 * bij loslaten gaat de nieuwe volgorde naar de server. Escape zet alles terug.
 */
function TodayList({
  order,
  editing,
  name,
  render,
  onOrder,
  onRemove,
  isEmpty,
  onFocus,
}: ListProps) {
  const h = appText.value.home;
  const list = useRef<HTMLDivElement>(null);
  const drag = useRef<ListDrag | null>(null);
  const [dragOrder, setDragOrderState] = useState<WidgetId[] | null>(null);
  const dragOrderRef = useRef<WidgetId[] | null>(null);
  const setDragOrder = (next: WidgetId[] | null) => {
    dragOrderRef.current = next;
    setDragOrderState(next);
  };
  const [ghost, setGhost] = useState<{ id: WidgetId; x: number; y: number } | null>(null);
  const shown = dragOrder ?? order;
  const detach = useRef<(() => void) | null>(null);
  useEffect(() => () => detach.current?.(), []);

  const indexAt = (y: number, id: WidgetId): number => {
    let index = 0;
    for (const frame of Array.from(
      list.current?.querySelectorAll<HTMLElement>(':scope > [data-widget]') ?? [],
    )) {
      if (frame.dataset.widget === id) continue;
      const r = frame.getBoundingClientRect();
      if (r.top + r.height / 2 < y) index += 1;
    }
    return index;
  };

  const finish = (save: boolean) => {
    detach.current?.();
    detach.current = null;
    const d = drag.current;
    drag.current = null;
    setGhost(null);
    const next = dragOrderRef.current;
    setDragOrder(null);
    if (save && d && next && next.join(',') !== d.start.join(',')) {
      onFocus(d.id, 'drag');
      onOrder(next, d.id);
    }
  };

  const onPointerDown = (e: PointerEvent, id: WidgetId) => {
    if (e.button !== 0) return;
    e.preventDefault();
    detach.current?.();
    drag.current = { id, pointerId: e.pointerId, start: order };
    setDragOrder(order);
    setGhost({ id, x: e.clientX, y: e.clientY });
    let lastY = e.clientY;
    let raf = 0;
    const move = (ev: PointerEvent) => {
      const d = drag.current;
      if (!d || ev.pointerId !== d.pointerId) return;
      lastY = ev.clientY;
      setGhost({ id: d.id, x: ev.clientX, y: ev.clientY });
      const at = indexAt(ev.clientY, d.id);
      const base = dragOrderRef.current ?? d.start;
      if (base.indexOf(d.id) !== at) {
        setDragOrder(placeWidget({ main: base, side: [] }, d.id, 'main', at).main);
      }
    };
    const up = (ev: PointerEvent) => {
      if (ev.pointerId === drag.current?.pointerId) finish(true);
    };
    const cancelDrag = (ev: PointerEvent) => {
      if (ev.pointerId === drag.current?.pointerId) finish(false);
    };
    const keyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      finish(false);
    };
    // Rustig meescrollen bij de rand van het scherm.
    const tick = () => {
      if (lastY < 72) window.scrollBy(0, -10);
      else if (lastY > window.innerHeight - 72) window.scrollBy(0, 10);
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancelDrag);
    window.addEventListener('keydown', keyDown);
    detach.current = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancelDrag);
      window.removeEventListener('keydown', keyDown);
      window.cancelAnimationFrame(raf);
    };
  };

  const step = (id: WidgetId, dir: -1 | 1) => {
    onFocus(id, dir < 0 ? 'up' : 'down');
    onOrder(moveWidget({ main: order, side: [] }, id, dir).main, id);
  };

  return (
    <div ref={list} class="home-list-col">
      {shown.map((id, at) => {
        const label = name(id);
        const hidden = !editing && isEmpty(id);
        return (
          <section
            key={id}
            class={`home-card${ghost?.id === id ? ' is-active' : ''}`}
            data-widget={id}
            data-size="L"
            hidden={hidden}
            aria-label={editing ? label : undefined}
          >
            {editing ? (
              <div class="home-list-tools">
                <button
                  type="button"
                  class="home-tool home-grip"
                  aria-label={t(h.drag, { name: label })}
                  title={t(h.drag, { name: label })}
                  data-action="drag"
                  onPointerDown={(e) => onPointerDown(e, id)}
                >
                  <Icon id="grip-vertical" size={20} />
                </button>
                <span class="home-tool-name">{label}</span>
                <button
                  type="button"
                  class="home-tool"
                  aria-label={t(h.moveUp, { name: label })}
                  title={t(h.moveUp, { name: label })}
                  data-action="up"
                  disabled={at <= 0}
                  onClick={() => step(id, -1)}
                >
                  <Icon id="chevron-up" size={20} />
                </button>
                <button
                  type="button"
                  class="home-tool"
                  aria-label={t(h.moveDown, { name: label })}
                  title={t(h.moveDown, { name: label })}
                  data-action="down"
                  disabled={at >= shown.length - 1}
                  onClick={() => step(id, 1)}
                >
                  <Icon id="chevron-down" size={20} />
                </button>
                <button
                  type="button"
                  class="home-tool home-remove"
                  aria-label={t(h.remove, { name: label })}
                  title={t(h.remove, { name: label })}
                  data-action="remove"
                  onClick={() => onRemove(id)}
                >
                  <Icon id="x" size={20} />
                </button>
              </div>
            ) : null}
            <div
              class="home-card-body"
              data-card-body={id}
              data-quiet={h.quiet}
              inert={editing}
              aria-hidden={editing ? 'true' : undefined}
            >
              {render(id, 'L')}
            </div>
          </section>
        );
      })}
      {ghost ? (
        <div
          class="widget-ghost"
          style={{ left: `${ghost.x}px`, top: `${ghost.y}px` }}
          aria-hidden="true"
        >
          <Icon id="grip-vertical" size={18} /> {name(ghost.id)}
        </div>
      ) : null}
    </div>
  );
}
